import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Loader2, RefreshCw, Eye, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Switch } from '../../components/ui/switch';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin/catalog`;

const GovCatalogImport = () => {
  const [file, setFile] = useState(null);
  const [isDesContract, setIsDesContract] = useState(true);
  const [categoryOverride, setCategoryOverride] = useState('');
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState([]);

  useEffect(() => { document.title = 'Catalog Import · USKE Procurement Portal'; }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/imports`);
      setHistory(res.data || []);
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const callApi = async (endpoint) => {
    if (!file) return toast({ title: 'Pick an .xlsx file first', variant: 'destructive' });
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('is_des_contract', String(isDesContract));
      if (categoryOverride) fd.append('category_override', categoryOverride);
      const res = await axios.post(`${API}/${endpoint}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      if (endpoint === 'import') {
        toast({ title: 'Catalog updated', description: `${res.data.created_count} created · ${res.data.updated_count} updated` });
        fetchHistory();
      }
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally { setImporting(false); }
  };

  return (
    <div className="space-y-4" data-testid="catalog-import-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 flex items-center gap-2">
          <FileSpreadsheet className="h-7 w-7 text-blue-700" /> Catalog Import
        </h1>
        <p className="text-sm text-slate-500">Upload an .xlsx file with columns <code>name, description, category, price, stock, image</code>. Existing SKUs are updated; new ones are created.</p>
      </div>

      <Card className="border-2">
        <CardHeader className="pb-2"><CardTitle className="text-base">Upload</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label htmlFor="f">Excel file (.xlsx)</Label>
            <Input id="f" type="file" accept=".xlsx,.xlsm" onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} data-testid="catalog-file-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex items-center gap-2">
              <Switch id="des" checked={isDesContract} onCheckedChange={setIsDesContract} />
              <Label htmlFor="des" className="cursor-pointer">DES contract item (sets contract_price + visible in gov catalog)</Label>
            </div>
            <div>
              <Label htmlFor="co">Category override <span className="text-slate-400 text-[11px]">(blank = use sheet column)</span></Label>
              <Input id="co" value={categoryOverride} onChange={(e) => setCategoryOverride(e.target.value)} placeholder="e.g. batteries" data-testid="catalog-category-override" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => callApi('preview')} disabled={!file || importing} variant="outline" data-testid="catalog-preview-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />} Preview (dry-run)
            </Button>
            <Button onClick={() => callApi('import')} disabled={!file || importing} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="catalog-import-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />} Import
            </Button>
          </div>
        </CardContent>
      </Card>

      {preview && (
        <Card className="border-2" data-testid="catalog-preview-card">
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {preview.dry_run ? 'Preview' : 'Import complete'} — {preview.filename}</CardTitle></CardHeader>
          <CardContent className="text-sm space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center">
              <div className="p-2 bg-emerald-50 rounded"><div className="text-2xl font-bold text-emerald-700">{preview.created_count}</div><div className="text-xs text-emerald-700">Created</div></div>
              <div className="p-2 bg-blue-50 rounded"><div className="text-2xl font-bold text-blue-700">{preview.updated_count}</div><div className="text-xs text-blue-700">Updated</div></div>
              <div className="p-2 bg-rose-50 rounded"><div className="text-2xl font-bold text-rose-700">{preview.errors?.length || 0}</div><div className="text-xs text-rose-700">Errors</div></div>
              <div className="p-2 bg-slate-50 rounded"><div className="text-2xl font-bold text-slate-700">{preview.categories?.length || 0}</div><div className="text-xs text-slate-700">Categories</div></div>
            </div>
            <div className="text-xs"><strong>Categories detected:</strong> {preview.categories?.join(', ') || '—'}</div>
            {preview.errors?.length > 0 && (
              <details><summary className="cursor-pointer text-rose-700 font-semibold flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> {preview.errors.length} errors</summary>
                <pre className="text-[10px] bg-slate-100 p-2 rounded mt-1 max-h-40 overflow-auto">{JSON.stringify(preview.errors, null, 2)}</pre>
              </details>
            )}
          </CardContent>
        </Card>
      )}

      <Card className="border-2">
        <CardHeader className="pb-2 flex flex-row items-center justify-between"><CardTitle className="text-base">Recent imports</CardTitle><Button variant="ghost" size="sm" onClick={fetchHistory}><RefreshCw className="h-3.5 w-3.5" /></Button></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr><th className="text-left p-2">When</th><th className="text-left p-2">File</th><th className="text-left p-2">By</th><th className="text-right p-2">Created</th><th className="text-right p-2">Updated</th><th className="text-right p-2">Errors</th></tr>
              </thead>
              <tbody>
                {history.length === 0 ? (<tr><td colSpan={6} className="p-4 text-center text-slate-400">No imports yet.</td></tr>) : history.map((h, i) => (
                  <tr key={i} className="border-t" data-testid={`import-history-${i}`}>
                    <td className="p-2 text-xs">{h.at ? new Date(h.at).toLocaleString() : '—'}</td>
                    <td className="p-2 font-mono text-xs">{h.filename}</td>
                    <td className="p-2 text-xs">{h.by}</td>
                    <td className="p-2 text-right text-emerald-700 font-semibold">{h.created_count}</td>
                    <td className="p-2 text-right text-blue-700 font-semibold">{h.updated_count}</td>
                    <td className="p-2 text-right text-rose-700">{h.errors_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GovCatalogImport;
